import React, { useEffect, useState } from 'react';
import { Button, Modal, Form } from 'react-bootstrap';
import {
  fetchOutsourceStages,
  updateOutsourceStage,
  createOutsourceStage,
  deleteOutsourceStage,
  fetchOutsourceRequestById,
} from '../../../services/api';

const STAGE_ORDER = [
  'Business Requirements',
  'Project Documentation',
  'Technical Questions',
  'Analysis',
  'Recommended Tech Stack',
  'Estimation',
  'Proposal',
  'Contract',
];

const OutsourceRequestProgress = ({ requestId, onProgressChange, setProgressTrigger }) => {
  const [stages, setStages] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentStage, setCurrentStage] = useState(null);
  const [documentLink, setDocumentLink] = useState('');
  const [request, setRequest] = useState(null);

  const loadStages = async () => {
    try {
      const allStages = await fetchOutsourceStages();
      const filtered = allStages.filter(
        (stage) => Number(stage.outsource_request) === Number(requestId)
      );
      setStages(filtered);
      updateProgress(filtered);
    } catch (err) {
      console.error('Error loading stages:', err);
    }
  };

  const updateProgress = (stagesData) => {
    try {
      const total = STAGE_ORDER.length; // always 8
      const completedCount = STAGE_ORDER.reduce((count, stageType) => {
        const key = stageType.toLowerCase().replace(/\s/g, '_');
        const matching = stagesData.find((s) => s.stage_type === key);
        return matching && typeof matching.document_link === 'string' && matching.document_link.trim() !== ''
          ? count + 1
          : count;
      }, 0);

      const percentage = total > 0 ? Math.round((completedCount / total) * 100) : 0;
      if (onProgressChange) onProgressChange(percentage);
      if (setProgressTrigger) setProgressTrigger((prev) => prev + 1);
    } catch (err) {
      console.error('Failed to update progress:', err);
      if (onProgressChange) onProgressChange(0);
    }
  };

  const loadRequest = async () => {
    try {
      const fetchedRequest = await fetchOutsourceRequestById(requestId);
      setRequest(fetchedRequest);
    } catch (err) {
      console.error('Error loading request:', err);
    }
  };

  useEffect(() => {
    if (requestId) {
      loadStages();
      loadRequest();
    }
  }, [requestId]);

  const handleStageClick = (stageType) => {
    const key = stageType.toLowerCase().replace(/\s/g, '_');
    const stage = stages.find((s) => s.stage_type === key);
    setCurrentStage(stage || { stage_type: key });
    setDocumentLink(stage?.document_link || '');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!requestId || isNaN(Number(requestId))) {
      alert('Invalid or missing request ID');
      return;
    }

    const payload = {
      stage_type: currentStage.stage_type,
      document_link: documentLink,
      outsource_request: Number(requestId),
      completed: true, // mark complete once link is present
    };

    try {
      if (currentStage.id) {
        await updateOutsourceStage(currentStage.id, payload);
      } else {
        await createOutsourceStage(payload);
      }
      setShowModal(false);
      await loadStages();
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save document link\n' + JSON.stringify(err, null, 2));
    }
  };

  const handleDelete = async () => {
    if (currentStage?.id && window.confirm('Are you sure you want to delete this stage?')) {
      try {
        await deleteOutsourceStage(currentStage.id);
        setShowModal(false);
        await loadStages();
      } catch (err) {
        console.error('Delete error:', err);
        alert('Failed to delete stage\n' + JSON.stringify(err, null, 2));
      }
    }
  };

  return (
    <div>
      <div className="container text-center">
        <div className="row gy-5">
          {STAGE_ORDER.map((stageType, index) => {
            const key = stageType.toLowerCase().replace(/\s/g, '_');
            const stage = stages.find((s) => s.stage_type === key);
            const hasLink = Boolean(stage?.document_link && stage.document_link.trim() !== '');

            return (
              <div key={index} className="col-12 col-md-3 mb-3">
                <div
                  className={`border rounded p-3 h-100 d-flex flex-column justify-content-between custom-card-height ${hasLink ? 'text-black' : '#F2F2F2'}`}
                  style={hasLink ? {
                    backgroundImage: 'url(https://res.cloudinary.com/dnbbm9vzi/image/upload/v1749666438/Main_Page_soqhly.png)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    color: 'white' // ensure text is readable
                  } : { backgroundColor: '#d6e8ff', color: 'black' }}
                >

                  <div>
                    <strong>{stageType}</strong>
                  </div>
                  <div className="mt-2 d-flex justify-content-between align-items-center">
                    <Button
                      variant="outline-primary"
                      className="fw-bold"
                      size="sm"
                      style={{ border: '1px solid #007bff' }}
                      onClick={() => handleStageClick(stageType)}
                    >
                      {stage?.document_link ? 'View/Edit' : 'Add'}
                    </Button>

                    {hasLink && (
                      <a
                        href={stage.document_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open Document"
                        className="text-white text-decoration-none ms-2"
                      >
                        👁️
                      </a>
                    )}
                  </div>


                </div>
              </div>
            );
          })}
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="w-100 text-center fw-bold">{currentStage?.id ? 'Edit' : 'Add'} Document Link</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Document Link</Form.Label>
            <Form.Control
              type="url"
              value={documentLink}
              onChange={(e) => setDocumentLink(e.target.value)}
              placeholder="https://docs.google.com/..."
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          {currentStage?.id && (
            <Button variant="danger" onClick={handleDelete} className="fw-bold">
              Delete
            </Button>
          )}
          <Button variant="dark" onClick={() => setShowModal(false)} className="fw-bold">
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} className="fw-bold">
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OutsourceRequestProgress;



